import React, { memo, useMemo, useCallback } from 'react';

import { EShipmentTypes, TShipments } from 'services/shipments/types/shipments.types';

import extractEntriesNumber, {
  extractProductOffers,
} from 'services/shipments/helpers/extractEntriesNumber';
import extractQuantityProps from '../UpdateQuantity/helpers/extractQuantityProps.helper';
import useMultiSelect from '../../hooks/useMultiSelect.hook';
import CartLayout from './CartListLayout';
import DynamicLoader from 'routes/DynamicLoader';
import APP_CONFIG from 'config/App.config';
import { createMarkup } from 'utils/html.util';
import { DeliveryText, NowMessageBox } from './styles/CartList.styles';
import { Card } from '@mafc/trolly';

import useOrderSummary from 'common/OrderSummary/hooks/useOrderSummary.hook';

const SubTotalView = DynamicLoader(() => import('./snippets/SubTotal')) as any;
const DisplayExpressMessage = DynamicLoader(() =>
  import(
    /* webpackChunkName: "groceryChunk" */
    './snippets/DisplayExpressMessage'
  )
) as any;
// const FreeDeliveryMessage = DynamicLoader(() =>
//   import(
//     /* webpackChunkName: "groceryChunk" */
//     './snippets/FreeDeliveryMessage'
//   )
// ) as any;
const ShipmentHeader = DynamicLoader(() => import('./snippets/ShipmentHeader')) as any;
const CartListItem = DynamicLoader(() => import('../cartListItem')) as any;
const CartListActionsBar = DynamicLoader(() => import('../CartListActionsBar')) as any;
const EnableSubstitution = DynamicLoader(() =>
  import(
    /* webpackChunkName: "groceryChunk" */
    '../EnableSubstitution'
  )
) as any;

interface Props {
  shipments: TShipments;
  isLoadingEntry: (arg: number) => boolean;
  productIDs: any;
  setSelectItemsLink: (val: boolean) => void;
  isSelectItemsLink: boolean;
}

const CartList: React.FC<Props> = ({
  shipments,
  isLoadingEntry,
  productIDs,
  setSelectItemsLink,
  isSelectItemsLink,
}) => {
  const entryNumbers = useMemo(() => extractEntriesNumber(shipments), [shipments]);
  const productOffers = useMemo(() => extractProductOffers(shipments), [shipments]);
  const [selectedItems, { selectItem, selectAll, isSelected }] = useMultiSelect(
    APP_CONFIG.isNewEndpoint ? productOffers : entryNumbers
  );
  let shouldDisplayDelivery: boolean = false;
  let bannerData: any;

  const {
    // freeDeliveryMessage = '',
    subTotal: orderSummarySubTotal = null,
    // freeDeliveryThreshold = 0,
  } = useOrderSummary() || {};
  const currency = orderSummarySubTotal
    ? orderSummarySubTotal.currencyName || orderSummarySubTotal.currencyIso
    : '';

  const getShipmentPopulate = useCallback(
    ({ item, ...rest }: any) => {
      const offerId =
        item && item.product && item.product.mainOffer && item.product.mainOffer.code
          ? item.product.mainOffer.code
          : null;
      const obj = {
        productId: item.entryNumber,
        offerId,
      };
      const loadingEntryNumber = parseInt(
        `${item.entryNumber}${item.offerId ? item.offerId : ''}`,
        10
      );
      return (
        <CartListItem
          {...rest}
          item={item}
          selected={isSelected(obj)}
          loading={isLoadingEntry(loadingEntryNumber)}
          quantityUpdate={extractQuantityProps(item, offerId)}
        />
      );
    },
    [isLoadingEntry, isSelected]
  );
  const getShipmentsView: {
    elements: React.ReactNode | null;
    totalItems: number;
  } = useMemo(() => {
    if (!shipments) {
      return {
        elements: null,
        totalItems: 0,
      };
    }

    let totalItems = 0;

    return {
      elements: shipments.map(({ entries: shipmentsEntries, shipmentType, sellersEntries }) => {
        let subTotal = 0;
        if (shipmentType === EShipmentTypes.MARKETPLACE) {
          return sellersEntries.map(({ entries: shipmentsSellersEntries, shopId }) => {
            subTotal = 0;
            return (
              <React.Fragment key={`${shipmentType}${shopId}${totalItems}`}>
                <Card
                  mb={3}
                  p={[2, 3]}
                  style={{
                    boxShadow: 'none',
                  }}
                  borderRadius="3"
                >
                  {shipmentsSellersEntries.map((item, counter) => {
                    subTotal += item.totalPrice.value;
                    totalItems += 1;
                    const obj = {
                      lastItem: shipmentsSellersEntries.length === counter + 1,
                      item,
                      onSelect: selectItem,
                      offerCode: item.offerId,
                      isOffer: true,
                      isSelectItemsLink,
                    };
                    return (
                      <React.Fragment key={`${shipmentType}${item.product.code}${totalItems}`}>
                        {counter === 0 ? (
                          <ShipmentHeader
                            itemCount={shipmentsSellersEntries.length}
                            productType={item.shopName || shipmentType}
                            soldBy={''}
                            isMarketPlace
                          />
                        ) : (
                          ''
                        )}
                        {getShipmentPopulate(obj)}
                      </React.Fragment>
                    );
                  })}
                </Card>
                {!isSelectItemsLink && <SubTotalView subTotal={subTotal} currency={currency} />}
              </React.Fragment>
            );
          });
        }
        const shipmentEntries = [...shipmentsEntries];
        if (shipmentType === EShipmentTypes.STANDARD_FOOD) {
          shipmentEntries.reverse();
        }
        if (!!shipmentEntries.length) {
          return (
            <React.Fragment key={`${shipmentType}${totalItems}`}>
              <Card
                mb={3}
                p={[2, 3]}
                style={{
                  boxShadow: 'none',
                  // borderColor: '#E0E0E0',
                }}
                // border="1px solid"
                borderRadius="3"
              >
                {shipmentEntries.map((item, index) => {
                  subTotal += item.totalPrice.value;
                  const { serviceEntries = [] } = item;

                  if (serviceEntries.length) {
                    const serviceTotal = serviceEntries.reduce(
                      (counter, serviceEntry) => counter + serviceEntry.basePrice.value,
                      0
                    );
                    subTotal += serviceTotal;
                  }

                  totalItems += 1;
                  const obj = {
                    lastItem: shipmentEntries.length === index + 1,
                    item,
                    onSelect: selectItem,
                    offerCode: '',
                    isOffer: false,
                    isSelectItemsLink,
                  };
                  return (
                    <React.Fragment key={`${shipmentType}${item.product.code}${totalItems}`}>
                      {index === 0 ? (
                        <>
                          <ShipmentHeader
                            itemCount={shipmentEntries.length}
                            productType={shipmentType}
                          />
                          {productIDs &&
                            !isSelectItemsLink &&
                            shipmentType === EShipmentTypes.STANDARD_FOOD && <EnableSubstitution />}
                          {shipmentType === EShipmentTypes.STANDARD_FOOD &&
                            !isSelectItemsLink &&
                            !shouldDisplayDelivery &&
                            shipments &&
                            shipments[0].description && (
                              <DisplayExpressMessage
                                line1={shipments[0].description.line1}
                                line2={shipments[0].description.line2}
                              />
                            )}

                          {/* <FreeDeliveryMessage
                            freeDeliveryThreshold={freeDeliveryThreshold}
                            freeDeliveryMessage={freeDeliveryMessage}
                            shipmentType={shipmentType}
                          /> */}
                        </>
                      ) : (
                        ''
                      )}
                      {getShipmentPopulate(obj)}
                    </React.Fragment>
                  );
                })}
              </Card>

              {!isSelectItemsLink && <SubTotalView subTotal={subTotal} currency={currency} />}
            </React.Fragment>
          );
        }
        return <></>;
      }),
      totalItems,
    };
  }, [
    shipments,
    selectItem,
    isSelectItemsLink,
    currency,
    // freeDeliveryMessage,
    // freeDeliveryThreshold,
    productIDs,
    shouldDisplayDelivery,
    getShipmentPopulate,
  ]);

  const displayDeliveryBanner = () => {
    if (!shipments || shipments.length === 0) {
      return;
    }
    shipments.forEach(shipment => {
      if (shipment.hideDeliveryCost) {
        shouldDisplayDelivery = true;
        bannerData = shipment.description;
      }
    });
    if (shouldDisplayDelivery && bannerData) {
      return (
        <NowMessageBox margin={15}>
          <DeliveryText>
            {bannerData.line1 && <div dangerouslySetInnerHTML={createMarkup(bannerData.line1)} />}
            {bannerData.line2 && <div dangerouslySetInnerHTML={createMarkup(bannerData.line2)} />}
          </DeliveryText>
        </NowMessageBox>
      );
    }
  };

  return (
    <CartLayout>
      {/* select all products checkbox */}

      {getShipmentsView.totalItems && getShipmentsView.totalItems > 1 ? (
        <CartListActionsBar
          totalItems={getShipmentsView.totalItems}
          selectAll={selectAll}
          checked={entryNumbers.length === selectedItems.length}
          selectedItems={selectedItems}
          variant="header"
          isSelectItemsLink={!!isSelectItemsLink}
          setSelectItemsLink={setSelectItemsLink}
        />
      ) : null}
      {displayDeliveryBanner()}

      {getShipmentsView.elements}
      {getShipmentsView.totalItems && getShipmentsView.totalItems > 1 && isSelectItemsLink && (
        <CartListActionsBar
          selectAll={selectAll}
          checked={entryNumbers.length === selectedItems.length}
          selectedItems={selectedItems}
          variant="footer"
          isSelectItemsLink={!!isSelectItemsLink}
          setSelectItemsLink={setSelectItemsLink}
        />
      )}
    </CartLayout>
  );
};

export default memo(CartList);
